import { setup, assign, fromPromise } from 'xstate';
import axios from '../../axiosConfig';
import {
  Context,
  Event,
  State,
  loginEvent,
  EventType,
  resetPasswordRequestEvent,
  resetPasswordEvent,
  oauthLoginEvent,
  LoginResponse,
} from './auth.fsm.types';
import { AxiosError, AxiosResponse } from 'axios';
import { signInWithGoogle } from './googleSignIn';
import { signInWithMicrosoft } from './microsoftSignIn';
import { GOOGLE_CLIENT_ID, MICROSOFT_CLIENT_ID } from './ssoClientConfig';

const MACHINE_ID = 'AuthFSM';

export const authFSM = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
  },
  actors: {
    authenticateUser: fromPromise(
      async ({ input }: { input: { email: string; password: string } }) => {
        const res = await axios.post('/api/auth/login', {
          email: input.email,
          password: input.password,
        });
        return res;
      },
    ),
    /**
     * Real as of 2026-10-02: avrio-backend#106/#107 shipped POST
     * /api/auth/sso-login and real, live client ids in every environment
     * (see ssoClientConfig.ts). Gets a real ID token from the provider's own
     * SDK, then exchanges it the same way password login exchanges a
     * password - the backend verifies the token (SsoTokenVerifier), never
     * this client.
     *
     * Deliberately not wrapped in a try/catch that rewraps the error: a
     * rejection from signInWithGoogle/signInWithMicrosoft (the person
     * closed the prompt, the popup was blocked) and an AxiosError from the
     * backend call both just propagate to onError as-is.
     * resolveOAuthErrorMessage (oauthMessages.ts) already falls back to
     * `error.message` for anything that isn't one of the backend's two
     * named codes, which is exactly what a client-side sign-in failure is.
     */
    authenticateWithProvider: fromPromise(
      async ({
        input,
      }: {
        input: { provider: 'google' | 'microsoft' };
      }): Promise<AxiosResponse<LoginResponse>> => {
        const idToken =
          input.provider === 'google'
            ? await signInWithGoogle(GOOGLE_CLIENT_ID)
            : await signInWithMicrosoft(MICROSOFT_CLIENT_ID);

        return axios.post('/api/auth/sso-login', {
          provider: input.provider,
          idToken,
        });
      },
    ),
    resetPasswordRequest: fromPromise(
      async ({ input }: { input: { email: string } }) => {
        const res = await axios.post('/api/auth/reset-password/request', {
          email: input.email,
        });
        return res;
      },
    ),
    resetPassword: fromPromise(
      async ({ input }: { input: { password: string; token: string } }) => {
        const res = await axios.post(
          '/api/auth/reset-password',
          {
            password: input.password,
          },
          {
            headers: {
              Authorization: `Bearer ${input.token}`,
            },
          },
        );
        return res;
      },
    ),
  },
}).createMachine({
  id: MACHINE_ID,
  initial: State.INITIAL,
  context: {
    isLoading: false,
    token: null,
    navigate: undefined,
    error: undefined,
  },
  states: {
    [State.INITIAL]: {
      on: {
        [EventType.LOGIN]: {
          target: State.AUTHENTICATING,
          actions: assign(({ event }) => ({
            isLoading: true,
            navigate: event.payload.navigate,
          })),
        },
        [EventType.RESET_PASSWORD_REQUEST]: {
          target: State.RESET_PASSWORD_REQUEST,
          actions: assign(() => ({
            isLoading: true,
          })),
        },
        [EventType.RESET_PASSWORD]: {
          target: State.RESET_PASSWORD,
          actions: assign(({ event }) => ({
            isLoading: true,
            navigate: event.payload.navigate,
          })),
        },
        [EventType.OAUTH_LOGIN]: {
          target: State.OAUTH_AUTHENTICATING,
          actions: assign(({ event }) => ({
            isLoading: true,
            navigate: event.payload.navigate,
            provider: event.payload.provider,
          })),
        },
      },
    },
    [State.RESET_PASSWORD]: {
      invoke: {
        src: 'resetPassword',
        input: ({ event }) => ({
          password: (event as resetPasswordEvent).payload.password,
          token: (event as resetPasswordEvent).payload.token,
        }),
        onDone: {
          target: State.PASSWORD_RESETED,
          actions: assign({
            isLoading: false,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.RESET_PASSWORD_REQUEST]: {
      invoke: {
        src: 'resetPasswordRequest',
        input: ({ event }) => ({
          email: (event as resetPasswordRequestEvent).payload.email,
        }),
        onDone: {
          target: State.EMAIL_SENT,
          actions: assign({
            isLoading: false,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.AUTHENTICATING]: {
      invoke: {
        src: 'authenticateUser',
        input: ({ event }) => ({
          email: (event as loginEvent).payload.email,
          password: (event as loginEvent).payload.password,
        }),
        onDone: {
          target: [State.AUTHENTICATED],
          actions: assign({
            isLoading: false,
            error: undefined,
            // A prior OAuth attempt in this same session could have left
            // these set; password login never carries either.
            justLinked: undefined,
            provider: undefined,
            token: ({ event }) => {
              const token = event.output.data.token;
              localStorage.setItem('token', token);
              return token;
            },
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.OAUTH_AUTHENTICATING]: {
      invoke: {
        src: 'authenticateWithProvider',
        input: ({ event }) => ({
          provider: (event as oauthLoginEvent).payload.provider,
        }),
        onDone: {
          target: [State.AUTHENTICATED],
          actions: assign({
            isLoading: false,
            error: undefined,
            justLinked: ({ event }) => Boolean(event.output.data.justLinked),
            token: ({ event }) => {
              const token = event.output.data.token;
              localStorage.setItem('token', token);
              return token;
            },
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.AUTHENTICATED]: {
      on: {
        LOGOUT: {
          target: State.INITIAL,
          actions: assign({
            token: null,
          }),
        },
      },
      always: {
        target: State.INITIAL,
        actions: ({ context }) => {
          if (context.navigate) {
            context.navigate('/dashboard');
          }
        },
      },
    },
    [State.PASSWORD_RESETED]: {
      always: {
        target: State.INITIAL,
        actions: ({ context }) => {
          if (context.navigate) {
            context.navigate('/auth/login');
          }
        },
      },
    },
    [State.EMAIL_SENT]: {
      after: {
        200: {
          target: State.INITIAL,
        },
      },
    },
    [State.ERROR]: {
      after: {
        200: {
          target: State.INITIAL,
        },
      },
    },
  },
});
